"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePhilosophyOfForce() {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    title: "Intensity",
    description: "Maximum output is the only metric that matters."
  }, {
    title: "Discipline",
    description: "Consistency forged through relentless daily habit."
  }, {
    title: "Adaptation",
    description: "Evolving the body to overcome every resistance."
  }, {
    title: "Precision",
    description: "Form as the foundation of absolute physical power."
  }];
  return <section data-section-id="3518" id="the-philosophy-of-force" className="bg-background text-foreground relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {}
        <div ref={imageRef} className={`relative h-[70vh] lg:h-auto lg:min-h-[900px] overflow-hidden transition-all duration-1000 ease-out ${imageVisible ? "opacity-100" : "opacity-0"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_intense_training.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_intense_training.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/athlete_intense_training.png-wvc-sizes" alt="High-intensity athlete training with heavy iron in a dark gym" loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out ${imageVisible ? "scale-100" : "scale-105"}`} />
          <div className="absolute inset-0 bg-background/10" />
        </div>

        {}
        <div ref={contentRef} className="relative bg-background flex items-center">
          <div className="w-full px-6 py-16 sm:px-10 sm:py-20 lg:px-24 lg:py-24 relative">
            {}
            <div className={`hidden lg:flex absolute left-8 top-24 bottom-24 items-start transition-all duration-1000 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-mono text-xs uppercase text-muted-foreground tracking-[0.35em] whitespace-nowrap" style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)"
            }}>
                THE FORCE PROTOCOL &mdash; <span className="font-default not-italic">力</span>
              </span>
            </div>

            {}
            <div className={`lg:hidden mb-8 transition-all duration-700 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-mono text-xs uppercase text-muted-foreground tracking-[0.35em]">
                THE FORCE PROTOCOL &mdash; <span className="font-default">力</span>
              </span>
            </div>

            <div className="max-w-xl">
              {}
              <h2 className={`font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground transition-all duration-1000 delay-100 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <span className="block">We do not</span>
                <span className="block italic text-primary">train bodies.</span>
                <span className="block">We engineer force.</span>
              </h2>

              {}
              <div className={`mt-12 space-y-6 transition-all duration-1000 delay-200 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <p className="font-default text-base sm:text-lg leading-relaxed text-muted-foreground">
                  Peak Force Fitness was built as an industrial performance lab for those who view their physical potential as a project to be optimized. Here, training is not a hobby &mdash; it is a high-stakes engineering discipline.
                </p>

                <Separator className="bg-border" />

                <p className="font-default text-base sm:text-lg leading-relaxed text-muted-foreground">
                  Our programs are designed by elite coaches who prioritize mechanical efficiency and raw output. We strip away the noise to focus on the fundamental movements that build structural integrity and explosive power.
                </p>

                <Separator className="bg-border" />

                <p className="font-default text-base sm:text-lg leading-relaxed text-muted-foreground">
                  Every athlete at Peak Force enters as a machine in development &mdash; pushing past limits, refining technique, and building a physique that is as functional as it is formidable.
                </p>
              </div>

              {}
              <div className={`mt-16 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8 transition-all duration-1000 delay-300 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                {pillars.map((pillar, i) => <div data-index={i} key={pillar.title} className="space-y-2">
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-xs text-primary">
                        0{i + 1}
                      </span>
                      <h3 className="font-default font-bold text-xl text-foreground">
                        {pillar.title}
                      </h3>
                    </div>
                    <p className="font-default text-sm leading-relaxed text-muted-foreground pl-7">
                      {pillar.description}
                    </p>
                  </div>)}
              </div>

              {}
              <div className={`mt-16 transition-all duration-1000 delay-500 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <Button asChild variant="link" className="px-0 h-auto text-primary hover:text-primary/80 font-default font-bold text-base group">
                  <Link to="/TheBlueprintPage">
                    View Our Methodology
                    <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}